// Ending a session (logout) is this one operation, whichever screen or partner it starts from. Once it
// has finished, nothing of the session is left on the device or can still be sent: no records,
// mapping drafts, pending saves, queued requests, credentials, device identifiers, images or service
// worker bookkeeping. It ends on the sign-in screen, loaded afresh.
//
// Other tabs of the app end their session too (watchForSessionEnd).

import { getActivePinia } from 'pinia'
import Db from '@/stores/db'
import { useDataSync } from '@/composables/useDataSync.js'
import { clearWorkerStorage, deleteDatabase, endWorkerSession } from '@/helpers/offlineSupport.js'
import { markSessionEnded, sessionEnded } from '@/helpers/sessionState.js'
import { navigateToSignIn } from '@/helpers/navigation.js'

const CHANNEL_NAME = 'sasa-session'

let ending = null

// Resolves once everything is cleared, just as the sign-in screen is loading. Asking again while it
// runs waits for the same logout.
export function endSession() {
  ending ??= _endSession()
  return ending
}

async function _endSession() {
  const pinia = getActivePinia()

  // Every step runs whatever happened in the one before, and the agent always reaches sign-in:
  // once logout is confirmed, it is not left half done
  try {
    // 1. From here on nothing of this session is written to the device or sent: persisted stores,
    //    IndexedDB and mapping drafts check this, and so do requests still on their way
    markSessionEnded()
    stopSync()

    // 2. What the service worker holds -- its queue, the user, key and device it replays for, and
    //    its bookkeeping -- cleared by the worker, which confirms; by this page when it cannot
    await clearWorkerState()

    // 3. What this page holds. The screen is left first, so nothing on it reads the stores as they
    //    empty.
    await leaveScreen(pinia)
    resetStores(pinia)
    await clearDatabase()
    clearWebStorage()

    // 4. Other tabs leave too (localStorage.clear above also tells them, through a storage event)
    announce()
  }
  finally {
    navigateToSignIn()
  }
}

async function clearWorkerState() {
  let outcome
  try {
    outcome = await endWorkerSession()
  }
  catch (error) {
    console.warn('Could not ask the service worker to end the session', error)
  }
  if (outcome !== 'cleared') await clearWorkerStorage().catch((error) => console.warn('Could not clear the service worker storage', error))
}

// Ends this tab's session when another tab, or the service worker, says the session has ended. The
// device's storage is cleared already; this tab only lets go of what it holds in memory, without
// writing it back, and loads the sign-in screen.
export function watchForSessionEnd() {
  if (typeof window === 'undefined') return
  const pinia = getActivePinia()

  const leave = async () => {
    if (sessionEnded()) return
    markSessionEnded()
    stopSync()
    await leaveScreen(pinia)
    resetStores(pinia)
    navigateToSignIn()
  }

  channel()?.addEventListener('message', (event) => {
    if (event.data?.type === 'sessionEnded') leave()
  })
  window.addEventListener('storage', (event) => {
    if (event.key === null && event.storageArea === window.localStorage) leave()
  })
  navigator.serviceWorker?.addEventListener('message', (event) => {
    if (event.data?.type === 'sessionEnded') leave()
  })
}

function stopSync() {
  try {
    useDataSync().stopWatchSync()
  }
  catch (error) {
    console.warn('Could not stop syncing', error)
  }
}

// To the sign-in route within the app, so the screen on show (a profile, a farmer) is gone before
// the stores it reads are emptied. The router is the one main.js gives every store.
async function leaveScreen(pinia) {
  try {
    await pinia?._s.get('userStore')?.router?.replace('/auth')
  }
  catch (error) {
    console.warn('Could not leave the screen before logout', error)
  }
}

// Every store, emptied in memory. Nothing is written back: persisted state goes through
// sessionState's persistedStateStorage, which writes nothing once the session has ended.
function resetStores(pinia) {
  pinia?._s.forEach((store) => {
    try {
      store.$reset()
    }
    catch {
      // A store with no $reset of its own; its collection and pending saves are emptied below
    }
    if (Array.isArray(store.collection)) store.collection = []
    if (Array.isArray(store.pendingSaves)) store.pendingSaves = []
  })
}

// The app's IndexedDB records: device identifiers, and images and documents kept on the device
async function clearDatabase() {
  try {
    await Db.clearAll()
  }
  catch (error) {
    console.warn('Could not clear the app database; deleting it', error)
    await deleteDatabase(Db.NAME)
  }
}

function clearWebStorage() {
  if (typeof window === 'undefined') return

  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      storage?.clear()
    }
    catch (error) {
      console.warn('Could not clear web storage', error)
    }
  }
}

let broadcastChannel

function channel() {
  if (typeof BroadcastChannel === 'undefined') return null
  broadcastChannel ??= new BroadcastChannel(CHANNEL_NAME)
  return broadcastChannel
}

function announce() {
  try {
    channel()?.postMessage({ type: 'sessionEnded' })
  }
  catch (error) {
    console.warn('Could not tell other tabs the session ended', error)
  }
}
